import * as React from 'react';
import { motion } from 'framer-motion';
import { ArrowDownRight, ArrowUpRight, type LucideIcon } from 'lucide-react';

import { cn } from '@/shared/lib/utils';
import { Card, CardContent } from '@/shared/ui/card';

export type KpiAccent = 'primary' | 'secondary' | 'success' | 'warning' | 'destructive';

interface KpiCardProps {
  /** Compact label (e.g. "Active Scaffolds"). */
  label: string;
  /** Value display string (already formatted). */
  value: string;
  /** Optional sub-line under the value. */
  sub?: string;
  /** Lucide icon shown in the corner chip. */
  icon: LucideIcon;
  /** Visual accent — colors the chip and the side rail. */
  accent?: KpiAccent;
  /** Optional delta (e.g. "+12.5%"). */
  delta?: string;
  /** True when the delta represents an improvement. */
  isIncrease?: boolean;
  /** Zero-based index for staggered animation. */
  index?: number;
}

const accentClasses: Record<KpiAccent, { rail: string; chip: string }> = {
  primary: {
    rail: 'tw-bg-primary',
    chip: 'tw-bg-primary/10 tw-text-primary',
  },
  secondary: {
    rail: 'tw-bg-secondary',
    chip: 'tw-bg-secondary/10 tw-text-secondary',
  },
  success: {
    rail: 'tw-bg-success',
    chip: 'tw-bg-success/10 tw-text-success',
  },
  warning: {
    rail: 'tw-bg-warning',
    chip: 'tw-bg-warning/10 tw-text-warning',
  },
  destructive: {
    rail: 'tw-bg-destructive',
    chip: 'tw-bg-destructive/10 tw-text-destructive',
  },
};

/**
 * KPI tile — used at the top of every dashboard.
 *
 * Stacks: left accent rail / icon chip / value / label / optional sub + delta.
 * Same visual rhythm across all three dashboards.
 */
export function KpiCard({
  label,
  value,
  sub,
  icon: Icon,
  accent = 'primary',
  delta,
  isIncrease,
  index = 0,
}: KpiCardProps) {
  const cls = accentClasses[accent];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.04, 0.4) }}
      className="tw-h-full"
    >
      <Card className="tw-relative tw-h-full tw-overflow-hidden">
        <div
          aria-hidden="true"
          className={cn(
            'tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-1',
            cls.rail,
          )}
        />
        <CardContent className="tw-p-5">
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
            <div
              className={cn(
                'tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-rounded-lg',
                cls.chip,
              )}
            >
              <Icon className="tw-h-4 tw-w-4" />
            </div>
            {delta ? (
              <div
                className={cn(
                  'tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium',
                  isIncrease
                    ? 'tw-text-success'
                    : 'tw-text-destructive',
                )}
              >
                {isIncrease ? (
                  <ArrowUpRight className="tw-h-3 tw-w-3" />
                ) : (
                  <ArrowDownRight className="tw-h-3 tw-w-3" />
                )}
                {delta}
              </div>
            ) : null}
          </div>
          <div className="tw-mt-3">
            <p className="tw-text-2xl tw-font-semibold tw-leading-tight tw-text-foreground">
              {value}
            </p>
            <p className="tw-mt-1 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-muted-foreground">
              {label}
            </p>
            {sub ? (
              <p className="tw-mt-1 tw-text-xs tw-text-muted-foreground">
                {sub}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
